import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Receipt
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const SalariesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('current');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockSalaries = [
        {
          id: 'SAL-2024-001',
          employeeId: 'EMP-001',
          employeeName: 'أحمد محمد',
          employeeNumber: '1001',
          position: 'محامي أول',
          basicSalary: 15000,
          housingAllowance: 3000,
          transportAllowance: 1000,
          bonuses: 2000,
          deductions: 500,
          netSalary: 20500,
          status: 'paid',
          paymentDate: '2024-12-01',
          month: '2024-12'
        },
        {
          id: 'SAL-2024-002',
          employeeId: 'EMP-002',
          employeeName: 'فاطمة علي',
          employeeNumber: '1002',
          position: 'محاسبة',
          basicSalary: 12000,
          housingAllowance: 2500,
          transportAllowance: 800,
          bonuses: 1500,
          deductions: 300,
          netSalary: 16500,
          status: 'paid',
          paymentDate: '2024-12-01',
          month: '2024-12'
        },
        {
          id: 'SAL-2024-003',
          employeeId: 'EMP-003',
          employeeName: 'خالد سالم',
          employeeNumber: '1003',
          position: 'مساعد إداري',
          basicSalary: 8000,
          housingAllowance: 1500,
          transportAllowance: 600,
          bonuses: 0,
          deductions: 200,
          netSalary: 9900,
          status: 'pending',
          paymentDate: null,
          month: '2024-12'
        },
        {
          id: 'SAL-2024-004',
          employeeId: 'EMP-004',
          employeeName: 'سارة محمود',
          employeeNumber: '1004',
          position: 'سكرتيرة تنفيذية',
          basicSalary: 9000,
          housingAllowance: 1800,
          transportAllowance: 700,
          bonuses: 1000,
          deductions: 0,
          netSalary: 12500,
          status: 'pending',
          paymentDate: null,
          month: '2024-12'
        },
        {
          id: 'SAL-2024-005',
          employeeId: 'EMP-005',
          employeeName: 'محمد عبدالله',
          employeeNumber: '1005',
          position: 'محامي',
          basicSalary: 13000,
          housingAllowance: 2600,
          transportAllowance: 900,
          bonuses: 1800,
          deductions: 400,
          netSalary: 17900,
          status: 'paid',
          paymentDate: '2024-12-01',
          month: '2024-12'
        }
      ];
      setSalaries(mockSalaries);
      setLoading(false);
    }, 500);
  }, []);

  // Calculate statistics
  const stats = {
    totalMonthly: salaries.reduce((sum, s) => sum + s.netSalary, 0),
    employeeCount: salaries.length,
    paidThisMonth: salaries.filter(s => s.status === 'paid').reduce((sum, s) => sum + s.netSalary, 0),
    unpaidThisMonth: salaries.filter(s => s.status === 'pending').reduce((sum, s) => sum + s.netSalary, 0),
    totalDeductions: salaries.reduce((sum, s) => sum + s.deductions, 0),
    totalBonuses: salaries.reduce((sum, s) => sum + s.bonuses, 0)
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'مدفوع',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      pending: {
        label: 'معلق',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Filter salaries
  const filteredSalaries = salaries.filter(salary => {
    const matchesSearch =
      salary.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      salary.employeeNumber.includes(searchTerm) ||
      salary.position.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || salary.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Handle delete
  const handleDelete = (salaryId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السجل؟')) {
      alert(`تم حذف السجل ${salaryId}`);
    }
  };

  // Handle export
  const handleExport = () => {
    alert('سيتم تصدير تقرير الرواتب قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الرواتب...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <DollarSign className="w-8 h-8" />
                <span>إدارة الرواتب</span>
              </h1>
              <p className="text-gray-600">نظام مهني لإدارة رواتب الموظفين</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/finance/salaries/batches')}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-purple-600 text-purple-600 rounded-lg font-bold hover:bg-purple-50 transition-all"
              >
                <FileText className="w-5 h-5" />
                <span>المسيرات</span>
              </button>
              {hasPermission('finance.export') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
                >
                  <Download className="w-5 h-5" />
                  <span>تصدير</span>
                </button>
              )}
              {hasPermission('finance.salary.edit') && (
                <button
                  onClick={() => navigate('/admin/finance/salaries/create')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>إصدار راتب</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 1️⃣ Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          {/* Total Monthly */}
          <div className="md:col-span-2 bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الرواتب الشهرية</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{stats.totalMonthly.toLocaleString()} ر.س</p>
              </div>
              <DollarSign className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Employee Count */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">عدد الموظفين</p>
                <p className="text-3xl font-black text-gray-700">{stats.employeeCount}</p>
              </div>
              <Users className="w-12 h-12 text-gray-400 opacity-20" />
            </div>
          </div>

          {/* Paid This Month */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">مدفوعة</p>
                <p className="text-2xl font-black text-green-600">{stats.paidThisMonth.toLocaleString()} ر.س</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Unpaid This Month */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">معلقة</p>
                <p className="text-2xl font-black text-yellow-600">{stats.unpaidThisMonth.toLocaleString()} ر.س</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600 opacity-20" />
            </div>
          </div>

          {/* Total Deductions */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الاستقطاعات</p>
                <p className="text-2xl font-black text-red-600">{stats.totalDeductions.toLocaleString()} ر.س</p>
              </div>
              <TrendingDown className="w-10 h-10 text-red-600 opacity-20" />
            </div>
          </div>

          {/* Total Bonuses */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المكافآت</p>
                <p className="text-2xl font-black text-[#C8A870]">{stats.totalBonuses.toLocaleString()} ر.س</p>
              </div>
              <Award className="w-10 h-10 text-[#C8A870] opacity-20" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث باسم، رقم وظيفي أو المسمى..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الحالات</option>
                <option value="paid">مدفوع</option>
                <option value="pending">معلق</option>
              </select>
            </div>

            {/* Month Filter */}
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="current">الشهر الحالي</option>
                <option value="last">الشهر الماضي</option>
                <option value="all">جميع الأشهر</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2️⃣ Salaries Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الموظف</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الرقم الوظيفي</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المسمى</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الراتب الأساسي</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">البدلات</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الخصومات</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الإجمالي</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الصرف</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSalaries.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد رواتب</p>
                    </td>
                  </tr>
                ) : (
                  filteredSalaries.map((salary) => {
                    const totalAllowances = salary.housingAllowance + salary.transportAllowance + salary.bonuses;
                    
                    return (
                      <tr key={salary.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-bold text-gray-800">{salary.employeeName}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{salary.employeeNumber}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{salary.position}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-800">{salary.basicSalary.toLocaleString()} ر.س</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-green-600">{totalAllowances.toLocaleString()} ر.س</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-red-600">{salary.deductions.toLocaleString()} ر.س</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-black text-[#1E3A8A] text-lg">{salary.netSalary.toLocaleString()} ر.س</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(salary.status)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">
                            {salary.paymentDate || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {/* View Details */}
                            <button
                              onClick={() => navigate(`/admin/finance/salaries/${salary.employeeId}`)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="عرض التفاصيل"
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </button>

                            {/* Edit */}
                            {hasPermission('finance.salary.edit') && (
                              <button
                                onClick={() => navigate(`/admin/finance/salaries/${salary.id}/edit`)}
                                className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="تعديل"
                              >
                                <Edit className="w-4 h-4 text-yellow-600" />
                              </button>
                            )}

                            {/* Delete */}
                            {hasPermission('finance.salary.delete') && (
                              <button
                                onClick={() => handleDelete(salary.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="حذف"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        {filteredSalaries.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">عرض {filteredSalaries.length} من أصل {salaries.length} موظف</span>
              <span className="font-bold text-[#1E3A8A]">
                إجمالي الرواتب المعروضة: {filteredSalaries.reduce((sum, s) => sum + s.netSalary, 0).toLocaleString()} ر.س
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SalariesPage;
